import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ShieldCheck,
  Store,
  HandHeart,
  Sparkles,
  Croissant,
  Carrot,
  Soup,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import BrowserFrame, { FAMILY_MEALS_SCREENS } from "../components/site/BrowserFrame";
import { getImpactStats } from "../lib/api";

const SITE = "https://misefamilymeals.ca";
const LOGO = "/images/logos/family-meals.png";
const roleIcons = [Store, HandHeart, Sparkles];
const rescuedIcons = [Croissant, Carrot, Soup];
// Screens in the "Inside the network" viewer, in the order of t.familyMeals.screens
const SCREENS = [
  FAMILY_MEALS_SCREENS.rescueBoard,
  FAMILY_MEALS_SCREENS.communityNeeds,
  FAMILY_MEALS_SCREENS.map,
  FAMILY_MEALS_SCREENS.impact,
];
const DOORS = [`${SITE}/register?type=restaurant`, `${SITE}/register?type=nonprofit`, `${SITE}/find-help`];
const ROTATE_MS = 2000;

const Eyebrow = ({ children, className = "" }) => (
  <p className={`text-[11px] font-semibold tracking-[0.22em] uppercase text-[#E8490F] ${className}`}>{children}</p>
);

const LiveDot = () => (
  <span className="relative flex h-2 w-2">
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22c55e] opacity-60" />
    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22c55e]" />
  </span>
);

const FamilyMeals = () => {
  const { t, lang } = useLanguage();
  const p = t.familyMeals;
  const [impact, setImpact] = useState(null);
  const [screen, setScreen] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = p.metaTitle;
    return () => {
      document.title = prevTitle;
    };
  }, [p.metaTitle]);

  // The server reads these from misefamilymeals.ca (cached for a minute).
  useEffect(() => {
    getImpactStats()
      .then(setImpact)
      .catch(() => setImpact(null));
  }, []);

  // Cycle the screen viewer every 2 seconds until someone picks a tab.
  useEffect(() => {
    if (!autoplay) return undefined;
    timer.current = setInterval(() => setScreen((s) => (s + 1) % SCREENS.length), ROTATE_MS);
    return () => clearInterval(timer.current);
  }, [autoplay]);

  const pick = (i) => {
    setAutoplay(false);
    setScreen(i);
  };

  const nf = new Intl.NumberFormat(lang === "fr" ? "fr-CA" : "en-CA", { maximumFractionDigits: 0 });
  const counters = [
    { value: impact ? nf.format(Number(impact.kg_rescued) || 0) : "–", label: p.impactLive.kgLabel, testId: "impact-kg" },
    { value: impact ? nf.format(Number(impact.meals_enabled) || 0) : "–", label: p.impactLive.mealsLabel, testId: "impact-meals" },
    { value: impact ? nf.format(Number(impact.co2e_avoided_kg) || 0) : "–", label: p.impactLive.co2Label, testId: "impact-co2" },
  ];

  return (
    <div data-testid="page-family-meals">
      {/* HERO: words left, the live Rescue Board and counters right */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute -right-40 -top-56 h-[520px] w-[520px] rounded-full bg-[#E8490F]/15 blur-3xl"
          aria-hidden
        />
        <div className="mise-container relative pt-10 md:pt-12 pb-12 md:pb-14">
          <div className="mx-auto max-w-5xl grid gap-10 lg:grid-cols-[5fr_7fr] items-center">
            <div className="min-w-0">
              <img src={LOGO} alt={p.heading} className="h-14 md:h-16 w-auto" />
              <span
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#E8490F]/40 bg-[#E8490F]/10 px-3.5 py-1.5 text-[10.5px] font-semibold tracking-[0.2em] uppercase text-[#E8490F]"
                data-testid="family-meals-badge"
              >
                <LiveDot />
                {p.badge}
              </span>
              <h1 className="mt-4 text-4xl md:text-[2.8rem] font-semibold tracking-tight leading-[1.06] [text-wrap:balance]">
                {p.subheading}
              </h1>
              <p className="mt-4 text-base text-white/70 leading-relaxed">{p.intro}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href={`${SITE}/register`} target="_blank" rel="noopener noreferrer" className="mise-pill-primary" data-testid="family-meals-join" data-track="family-meals-join">
                  {p.joinCta}
                  <ArrowRight size={16} />
                </a>
                <a href={SITE} target="_blank" rel="noopener noreferrer" className="mise-pill-secondary" data-testid="family-meals-site" data-track="family-meals-site">
                  {p.siteCta}
                  <ArrowUpRight size={16} />
                </a>
              </div>
              <p className="mt-4 inline-flex items-center gap-2 text-sm text-white">
                <Check size={16} className="text-[#22c55e]" />
                {p.free}
              </p>
            </div>

            <div className="min-w-0 grid gap-4">
              <BrowserFrame src={FAMILY_MEALS_SCREENS.hero} alt={p.heading} url="misefamilymeals.ca" testId="family-meals-hero-screenshot" />
              <div data-testid="family-meals-live-impact">
                <p className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.16em] uppercase text-[#22c55e]">
                  <LiveDot />
                  {p.impactLive.eyebrow}
                </p>
                <div className="mt-2.5 grid grid-cols-3 overflow-hidden rounded-2xl border border-[#333333] bg-[#151515]">
                  {counters.map((c, i) => (
                    <div key={c.testId} className={`px-4 py-3.5 ${i ? "border-l border-[#333333]" : ""}`}>
                      <p className="text-2xl md:text-3xl font-semibold tracking-tight text-white" data-testid={c.testId}>
                        {c.value}
                      </p>
                      <p className="text-xs text-white/50">{c.label}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-white/40 leading-relaxed">{p.impactLive.note}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEDIA: humble but visible */}
      <section className="py-10 md:py-12 border-t border-[#242424]" data-testid="family-meals-media">
        <div className="mise-container">
          <div className="mx-auto max-w-5xl text-center">
            <Eyebrow>{p.media.eyebrow}</Eyebrow>
            <blockquote className="mt-3 mise-serif mise-accent text-2xl md:text-[2rem] leading-snug [text-wrap:balance]">
              “{p.media.quote}”
            </blockquote>
            <p className="mt-2 text-xs text-white/45">{p.media.quoteBy}</p>
            <div className="mt-7 grid gap-4 md:grid-cols-2 text-left">
              {p.media.items.map((m, i) => (
                <a
                  key={m.href}
                  href={m.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative overflow-hidden rounded-2xl border border-[#E8490F]/25 bg-gradient-to-br from-[#221812] via-[#1a1a1a] to-[#181818] p-5 pl-6 transition-all hover:-translate-y-0.5 hover:border-[#E8490F]/60"
                  data-testid={`family-meals-press-${i}`}
                  data-track={`family-meals-press-${i}`}
                >
                  <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#E8490F] to-[#E8490F]/20" aria-hidden />
                  <p className="text-[10.5px] font-semibold tracking-[0.2em] uppercase text-[#E8490F]">
                    {m.outlet} <span className="text-white/35">· {m.date}</span>
                  </p>
                  <p className="mt-2.5 mise-serif text-xl md:text-[1.35rem] leading-snug text-white">{m.headline}</p>
                  <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-white/70 group-hover:text-white">
                    {p.media.read}
                    <ArrowUpRight size={14} className="text-[#E8490F]" />
                  </p>
                </a>
              ))}
            </div>
            <Link
              to="/media"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-white/70 hover:text-white"
              data-testid="family-meals-press-all"
              data-track="family-meals-press-all"
            >
              {p.media.all}
              <ArrowRight size={14} className="text-[#E8490F]" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS: three-step line */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="family-meals-how">
        <div className="mise-container">
          <div className="mx-auto max-w-5xl">
            <Eyebrow>{p.howEyebrow}</Eyebrow>
            <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-white">{p.howHeading}</h2>
            <ol className="relative mt-8 grid gap-6 md:grid-cols-3">
              <span
                className="pointer-events-none absolute left-[8%] right-[8%] top-[17px] hidden h-px bg-gradient-to-r from-[#E8490F] to-[#E8490F]/20 md:block"
                aria-hidden
              />
              {p.roles.map((r, i) => {
                const Icon = roleIcons[i];
                return (
                  <li key={r.title} className="relative">
                    <span className="relative z-10 flex h-[34px] w-[34px] items-center justify-center rounded-full border-2 border-[#E8490F] bg-[#111111] text-[#E8490F]">
                      <Icon size={15} />
                    </span>
                    <p className="mt-4 text-[10px] font-semibold tracking-[0.2em] uppercase text-white/45">{r.who}</p>
                    <h3 className="mt-1 font-semibold text-white">{r.title}</h3>
                    <p className="mt-1.5 text-sm text-white/65 leading-relaxed">{r.text}</p>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* INSIDE THE NETWORK: screens cycle every 2s until a tab is picked */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="family-meals-screens">
        <div className="mise-container">
          <div className="mx-auto max-w-5xl">
            <Eyebrow>{p.screensEyebrow}</Eyebrow>
            <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-white">{p.screensHeading}</h2>
            <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label={p.screensEyebrow}>
              {p.screens.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  role="tab"
                  aria-selected={screen === i}
                  onClick={() => pick(i)}
                  className={`relative overflow-hidden rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    screen === i
                      ? "border-[#E8490F] bg-[#E8490F]/12 text-white"
                      : "border-[#333333] bg-[#151515] text-white/65 hover:text-white hover:border-[#6a6a6a]"
                  }`}
                  data-testid={`family-meals-screen-tab-${i}`}
                >
                  {label}
                  {autoplay && screen === i && (
                    <span
                      key={`bar-${screen}`}
                      className="absolute bottom-0 left-0 h-[2px] bg-[#E8490F] animate-[fmTabProgress_2s_linear_forwards]"
                      aria-hidden
                    />
                  )}
                </button>
              ))}
            </div>
            <div className="relative mt-5" data-testid="family-meals-screen">
              {SCREENS.map((src, i) => (
                <div
                  key={src}
                  className={`transition-opacity duration-500 ${i === screen ? "relative opacity-100" : "absolute inset-0 opacity-0 pointer-events-none"}`}
                  aria-hidden={i !== screen}
                >
                  <BrowserFrame src={src} alt={p.screens[i]} url="misefamilymeals.ca" loading="eager" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SAFETY + WHAT GETS RESCUED */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="family-meals-safety">
        <div className="mise-container">
          <div className="mx-auto max-w-5xl grid gap-10 lg:grid-cols-2">
            <div className="min-w-0">
              <Eyebrow>{p.safetyEyebrow}</Eyebrow>
              <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-white [text-wrap:balance]">{p.safetyHeading}</h2>
              <ul className="mt-5">
                {p.safety.map((s) => (
                  <li key={s.title} className="flex gap-3 border-t border-[#2a2a2a] py-3">
                    <ShieldCheck size={18} className="mt-0.5 flex-shrink-0 text-[#22c55e]" />
                    <div>
                      <p className="font-semibold text-white text-[15px]">{s.title}</p>
                      <p className="mt-0.5 text-sm text-white/60 leading-relaxed">{s.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="min-w-0">
              <Eyebrow>{p.rescuedEyebrow}</Eyebrow>
              <div className="mt-4 grid gap-3">
                {p.rescued.map((r, i) => {
                  const Icon = rescuedIcons[i];
                  return (
                    <div key={r.title} className="flex gap-4 rounded-2xl border border-[#333333] bg-[#181818] p-4">
                      <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#E8490F]/15 text-[#E8490F]">
                        <Icon size={19} />
                      </span>
                      <div>
                        <p className="font-semibold text-white">{r.title}</p>
                        <p className="mt-0.5 text-sm text-white/60 leading-relaxed">{r.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THREE WAYS IN */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="family-meals-join-section">
        <div className="mise-container">
          <div className="mx-auto max-w-5xl">
            <Eyebrow>{p.joinEyebrow}</Eyebrow>
            <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-white [text-wrap:balance]">{p.joinHeading}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-[1fr_1fr_0.85fr]">
              {p.doors.map((d, i) => (
                <a
                  key={d.title}
                  href={DOORS[i]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group flex flex-col rounded-2xl border p-5 transition-colors ${
                    i === 0
                      ? "border-[#E8490F]/45 bg-gradient-to-br from-[#2b1810] to-[#181818] hover:border-[#E8490F]"
                      : "border-[#333333] bg-[#181818] hover:border-[#E8490F]/55"
                  }`}
                  data-testid={`family-meals-door-${i}`}
                  data-track={`family-meals-door-${i}`}
                >
                  <h3 className="text-lg font-semibold text-white">{d.title}</h3>
                  <p className="mt-1.5 flex-1 text-sm text-white/65 leading-relaxed">{d.text}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#E8490F]">
                    {d.cta}
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </a>
              ))}
            </div>
            <p className="mt-4 text-xs text-white/40 leading-relaxed">{p.note}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FamilyMeals;
